import '@react-pdf-viewer/core/lib/styles/index.css';

import { Button } from '@heroui/react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import React, { FC, Fragment, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { sortByOrder } from 'src/helpers';
import { GoodPracticesFirestoreResponse } from 'src/types';

import { Card, Error, Modal, Spinner } from '../../components';
import { useGetGoodPracticesFromFirestore, useQueryHelper } from '../../queries';
import { useLanguageState, useOrganizationState } from '../../state';
import { ContentContainer, Logo, Text, TextContainer, Title } from './GoodPractices.styled';

export const GoodPractices: FC = () => {
  const { t } = useTranslation();
  const { organizationData } = useOrganizationState();
  const { language: currentLang } = useLanguageState();
  const goodPracticesCollections = useGetGoodPracticesFromFirestore(organizationData.hotel);
  const { data: goodPractices, refetch } = goodPracticesCollections;
  const sortedGoodPractices = sortByOrder(goodPractices || []);
  const { isLoading, isError } = useQueryHelper({ queries: [goodPracticesCollections] });
  const [pdfModal, setPdfModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<GoodPracticesFirestoreResponse | null>(null);
  if (isLoading) {
    return (
      <Spinner global />
    );
  }

  if (isError) {
    return <Error info errorTitle="Brak dostępnych dobrych praktyk" action={refetch} />;
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | Dobre praktyki hotelowe</title>
        <meta name="description" content="Hotelspot - Dobre praktyki hotelowe" />
      </Helmet>
      <div>
        <Title>{t('good_practice_screen.title')}</Title>
        <div className="grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {sortedGoodPractices && sortedGoodPractices?.length > 0
           && sortedGoodPractices?.map((goodPractise: GoodPracticesFirestoreResponse) => (
             <Card
               key={goodPractise.id}
               image={goodPractise.cover}
               isVertical
               title={goodPractise.title[currentLang]}
               description={goodPractise.description[currentLang]}
               action={() => setShowModal(goodPractise)}
               logo={goodPractise.logo}
             />
           ))}
        </div>
        <Modal
          size="2xl"
          isOpen={showModal !== null}
          onClose={() => setShowModal(null)}
          title={showModal?.title[currentLang]}
        >
          <Fragment key="modal">
            <ContentContainer>
              <TextContainer>
                <Text>
                  {showModal?.logo && <Logo src={showModal?.logo} alt="logo" />}
                  {showModal?.description[currentLang]}
                </Text>
                {showModal?.fileUrl && (
                <Button
                  onPress={() => setPdfModal(true)}
                  color="primary"
                  variant="solid"
                >
                  {t('buttons.show_more')}
                </Button>
                )}
              </TextContainer>
            </ContentContainer>
          </Fragment>
        </Modal>
        <Modal
          size="full"
          isOpen={pdfModal}
          onClose={() => setPdfModal(false)}
          title={showModal?.title?.[currentLang] || ''}
        >
          <Fragment key="modal">
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
              <Viewer
                fileUrl={showModal?.fileUrl || ''}
                defaultScale={1}
              />
            </Worker>
          </Fragment>
        </Modal>
      </div>
    </>
  );
};
